import React from 'react';
import Box from '@mui/material/Box';
import { GridTable } from '../../Sidebar/SidebarStyles';
import { Alert, CircularProgress, Snackbar, TextField, Button } from '@mui/material';
import { fetchJobGenerationErrors } from '../../ApiSevices/ReportApis';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

interface JobErrorsProps {
    displaysidebar: boolean;
}

interface JobErrorsState {
    loading: boolean;
    errorMessages: string[];
    jobId: string;
    snackbar: {
        open: boolean;
        message: string;
        severity: 'error' | 'info' | 'success';
    };
    hasSearched: boolean;
}

class JobGenerationErrors extends React.Component<JobErrorsProps, JobErrorsState> {
    constructor(props: JobErrorsProps) {
        super(props);
        this.state = {
            loading: false,
            errorMessages: [],
            jobId: '',
            snackbar: {
                open: false,
                message: '',
                severity: 'info'
            },
            hasSearched: false
        };
    }

    handleSearch = async () => {
        const { jobId } = this.state;
        
        if (!jobId.trim()) {
            this.showSnackbar('Job ID cannot be empty', 'error');
            this.setState({ hasSearched: true });
            return;
        }

        this.setState({ 
            loading: true, 
            errorMessages: [],
            hasSearched: true 
        });
        
        try {
            const errors = await fetchJobGenerationErrors(jobId);
            this.setState({ errorMessages: errors });
            
            if (errors.length === 0) {
                this.showSnackbar('No errors found for this job', 'info');
            }
        } catch (error) {
            this.showSnackbar('Failed to fetch job errors. Please try again.', 'error');
            this.setState({ errorMessages: [] });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleJobIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        this.setState({
            jobId: value,
            errorMessages: [],
            hasSearched: false
        });
    };

    showSnackbar = (message: string, severity: 'error' | 'info' | 'success') => {
        this.setState({
            snackbar: {
                open: true,
                message,
                severity
            }
        });
    };

    handleCloseSnackbar = () => {
        this.setState(prevState => ({
            snackbar: {
                ...prevState.snackbar,
                open: false
            }
        }));
    };

    render() {
        const { displaysidebar } = this.props;
        const { loading, errorMessages, jobId, snackbar, hasSearched } = this.state;

        return (
            <>
                {loading && (
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        zIndex: 9999
                    }}>
                        <div className="loader"></div>
                    </Box>
                )}

                <GridTable displaysidebar={displaysidebar}>
                    <Box
                        sx={{
                            padding: '2px 0',
                            backgroundColor: '#f8f8f8',
                            borderBottom: '1px solid #ddd',
                        }}
                    >
                        <span
                            style={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: '#000',
                                marginLeft: '8px',
                                textTransform: 'uppercase',
                            }}
                        >
                            CHECKLIST ERROR LOG
                        </span>
                    </Box>
                    
                    <Box sx={{ p: 2 }}>
                        <Box display="flex" gap={2} alignItems="flex-start" mb={2}>
                            <TextField
                                name="jobId"
                                placeholder="Enter Job ID"
                                size="small"
                                fullWidth
                                value={jobId}
                                onChange={this.handleJobIdChange}
                                onKeyDown={(e) => e.key === 'Enter' && this.handleSearch()}
                                sx={{ 
                                    width: 350,
                                    '& .MuiOutlinedInput-root': { height: 30 }
                                }}
                            />

                            <Button
                                variant="contained"
                                onClick={this.handleSearch}
                                sx={{ 
                                    height: 30,
                                    px: 3,
                                    backgroundColor: '#2a327d',
                                    '&:hover': { backgroundColor: '#1a2270' }
                                }}
                            >
                                Search
                            </Button>
                        </Box>

                        {hasSearched && errorMessages.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Paper
                                    sx={{
                                        width: '100%',
                                        mb: 2,
                                        overflow: 'hidden',
                                        border: '0.5px solid #edebeb'
                                    }}
                                >
                                    <Box sx={{ maxHeight: 400, width: '100%', overflowX: 'auto' }}>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell
                                                            sx={{
                                                                fontSize: '12px',
                                                                fontWeight: 'bold',
                                                                textAlign: 'left',
                                                                backgroundColor: '#f5f5f5'
                                                            }}
                                                        >
                                                            ERROR MESSAGE
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {errorMessages.map((message, index) => (
                                                        <TableRow
                                                            key={index}
                                                            sx={{
                                                                backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                                                                '&:hover': { backgroundColor: '#f1f1f1' }
                                                            }}
                                                        >
                                                            <TableCell
                                                                sx={{
                                                                    fontSize: '12px',
                                                                    textAlign: 'left',
                                                                    whiteSpace: 'normal',
                                                                    wordBreak: 'break-word',
                                                                    padding: '8px 16px',
                                                                    lineHeight: 1.5
                                                                }}
                                                            >
                                                                {message}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                    
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={this.handleCloseSnackbar}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <Alert 
                            onClose={this.handleCloseSnackbar} 
                            severity={snackbar.severity}
                            sx={{ width: "100%" }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </GridTable>
            </>
        );
    }
}

export default JobGenerationErrors;